import { useFetchCaseTemplatesForAutocomplete } from "@/queries/useFetchCaseTemplatesForAutocomplete.ts";
import { Autocomplete, Box, Grid2 as Grid, TextField, Typography } from "@mui/material";
import { type ChangeEvent, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

type Option = {
    id: string;
    name: string;
};

const CaseTemplatesAutocomplete = () => {
    const { watch, setValue, formState } = useFormContext();
    const formValue = watch("caseTemplateId");
    const [searchString, setSearchString] = useState("");

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchCaseTemplatesForAutocomplete({
        searchString: searchString,
        isQueryEnabled: true,
        subQueryKey: "task-template-autocomplete",
    });

    const options: Option[] = useMemo(() => {
        if (data) {
            return data.map((company) => ({
                id: company.idUUID,
                name: company.name ?? "",
            }));
        }

        return [];
    }, [data]);

    const displayOption = data?.find((caseTemplate) => caseTemplate.idUUID === formValue);

    return (
        <Grid container>
            <Grid size={{ xs: 12, md: 6 }} sx={{ pr: 1 }}>
                {isError && <Typography variant={"h5"}>Error loading case templates.</Typography>}
                <Autocomplete
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Case template name"
                            value={searchString}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                if (event.target.value.length >= 2) {
                                    setSearchString(event.target.value);
                                }
                            }}
                        />
                    )}
                    options={options}
                    loading={isLoading || isRefetching}
                    getOptionLabel={(option: Option) => option.name ?? ""}
                    onChange={(_event, value) => {
                        if (value) {
                            setValue("caseTemplateId", value.id);
                        }
                    }}
                    autoHighlight
                    blurOnSelect
                    sx={{
                        "& .MuiAutocomplete-inputRoot": {
                            padding: "4px!important",
                        },
                        "& .MuiAutocomplete-input": {
                            paddingTop: "4px!important",
                            paddingBottom: "4px!important",
                        },
                    }}
                />
                <Typography sx={{ ml: 2, fontSize: "14px" }}>
                    Start Typing Case Template Name
                </Typography>
                {formState.errors.caseTemplateId !== undefined && (
                    <Box sx={{ color: "red", fontSize: "12px", ml: 1, mt: 1 }}>
                        You must select a case template.
                    </Box>
                )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                {displayOption && <Box sx={{ px: 2 }}>{displayOption.description}</Box>}
            </Grid>
        </Grid>
    );
};

export default CaseTemplatesAutocomplete;
